/**
 * Dev-only theme picker.
 * Visible only to andrew.dodd@jyrney.com on localhost. Writes the selected
 * brand name to localStorage under `__themeOverride`, which useCurrentTheme
 * reads on next render. Reloads the page so every component (including
 * one-time-initialized ones) picks up the new theme.
 */
import { Box, MenuItem, Select, Typography } from '@mui/material'
import { Theme } from 'api/models/Common/JyrneyEnums'
import { useSelector } from 'react-redux'

const STORAGE_KEY = '__themeOverride'
const CLEAR_VALUE = '__clear__'
const ALLOWED_EMAIL = 'andrew.dodd@jyrney.com'

const isLocalhost = () => {
  if (typeof window === 'undefined') return false
  const h = window.location.hostname
  return h === 'localhost' || h === '127.0.0.1' || h === '[::1]'
}

const ThemeSwitcher = () => {
  const currentUser = useSelector((state: any) => state.users?.user)
  const email: string | undefined = currentUser?.email

  if (email?.toLowerCase() !== ALLOWED_EMAIL) return null
  if (!isLocalhost()) return null

  const current =
    (typeof window !== 'undefined' &&
      window.localStorage.getItem(STORAGE_KEY)) ||
    ''

  const options: Array<{ label: string; value: string }> = [
    { label: 'Default (host/client)', value: CLEAR_VALUE },
    ...Object.entries(Theme).map(([k, v]) => ({ label: k, value: v })),
  ]

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 12,
        right: 12,
        zIndex: 2000,
        bgcolor: '#fff',
        border: '1px solid #d0d6dc',
        borderRadius: '8px',
        boxShadow: '0 6px 18px rgba(10, 37, 61, 0.12)',
        px: 1.25,
        py: 0.75,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: '#5a6670',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        Theme
      </Typography>
      <Select
        size="small"
        value={current || CLEAR_VALUE}
        onChange={(e) => {
          const v = e.target.value as string
          if (v === CLEAR_VALUE) {
            window.localStorage.removeItem(STORAGE_KEY)
          } else {
            window.localStorage.setItem(STORAGE_KEY, v)
          }
          window.location.reload()
        }}
        sx={{
          minWidth: 160,
          '& .MuiSelect-select': {
            py: 0.5,
            fontSize: 13,
            fontWeight: 600,
            color: '#00253d',
          },
        }}
      >
        {options.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
  )
}

export default ThemeSwitcher
